import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationService } from '../services/notificationService';
import { useAuth } from './AuthContext';

const NotificationsContext = createContext(null);
const POLL_MS = 30_000;

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications deve estar dentro de NotificationsProvider');
  return ctx;
};

// Poll leve. Não fetch while tab is hidden.
export const NotificationsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    try {
      setLoading(true);
      const data = await notificationService.list({ limit: 20 });
      setItems(data.rows || []);
      setUnreadCount(data.unreadCount ?? 0);
    } catch {
      /* silencioso — rede instável não deve vazar erro */
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (id) => {
    try {
      await notificationService.markAsRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* noop */
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setItems((prev) =>
        prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    refresh();
    const loop = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    timerRef.current = setInterval(loop, POLL_MS);
    document.addEventListener('visibilitychange', loop);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', loop);
    };
  }, [isAuthenticated, refresh]);

  return (
    <NotificationsContext.Provider
      value={{ items, unreadCount, loading, refresh, markAsRead, markAllAsRead }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
